import { Injectable } from '@angular/core';
import * as uuid from 'uuid';
import { BehaviorSubject, combineLatest, defer, EMPTY, merge, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, delay, distinctUntilChanged, map, retryWhen, scan, shareReplay, startWith, switchMap, take, tap } from 'rxjs/operators';
import { AppError } from '@shared/models';
import { UserApi } from '@shared-features/services/user-api.service';
import { BidChatApiService } from '@features/bid-chat/services/bid-chat-api.service';
import { BidChatFirebaseService, toIsoTime } from '@features/bid-chat/services/bid-chat-firebase.service';
import { BidChatDrawerService } from '@features/bid-chat/services/bid-chat-drawer.service';
import {
  BidChatHeader,
  BidChatMessage,
  BidChatRow,
  BidChatTarget,
  ChatConversationDto,
  ChatConversationRef,
  ChatConversationStatus,
  ChatMessageType,
  ChatOfferStatus,
} from '@features/bid-chat/models/bid-chat.model';
import { CUSTOMER_CHAT_SIDE, offerBody } from '@features/bid-chat/constants/bid-chat.constants';

export interface BidChatLogsState {
  active: BidChatRow[];
  closed: BidChatRow[];
  /** False until the first Firestore snapshot, so "no chats" never flashes early. */
  hasActiveSnapshot: boolean;
  /** False until `conversations/history` has answered. */
  hasClosedSnapshot: boolean;
}

export interface BidChatThreadState {
  phase: 'resolving' | 'ready' | 'error';
  /** new: no conversation yet, the first message opens it · live: OPEN · history: CLOSED, read-only. */
  mode: 'new' | 'live' | 'history' | null;
  isOpen: boolean;
  /** Server messages plus this browser's own sends that have not reached Firestore yet. */
  messages: BidChatMessage[];
  /** False until the first messages snapshot, so "Start a conversation" never flashes over a real thread. */
  messagesLoaded: boolean;
  /** `accepted` holds until Firestore shows the offer ACCEPTED, so the button can't be pressed twice. */
  acceptStatus: 'idle' | 'accepting' | 'accepted' | 'failed';
  /** The offer the last accept was for; only its card shows that accept's state. */
  acceptOfferId: string | null;
  header: BidChatHeader;
  /** The bid's status: the conversation doc's live one, else the one the thread was opened with. */
  bidStatus: string | null;
}

/** A Chat logs row opens its thread directly: it already carries the conversation, so nothing is resolved first. */
export const targetFromRow = (row: BidChatRow): BidChatTarget => ({
  shipmentId: row.shipmentId,
  counterpartName: row.counterpartName,
  // Known before the doc is read, so a quick first send bumps the right unread counter.
  counterpartKind: row.counterpartKind,
  bidStatus: row.bidStatus,
  conversation: { firestoreConversationId: row.firestoreConversationId, status: row.status },
});

/** The header from the bid the thread was opened from, until the conversation doc says more. */
export const headerFromTarget = (target: BidChatTarget): BidChatHeader => ({
  counterpartKind: target.counterpartKind ?? 'fleet',
  counterpartName: target.counterpartName,
  isVerified: target.isVerified,
  originalBid: target.originalBid,
});

const INITIAL_THREAD_STATE: BidChatThreadState = {
  phase: 'resolving',
  mode: null,
  isOpen: false,
  messages: [],
  messagesLoaded: false,
  acceptStatus: 'idle',
  acceptOfferId: null,
  header: { counterpartKind: 'fleet', counterpartName: '' },
  bidStatus: null,
};

/**
 * A chat is over once its bid is rejected or cancelled (or the backend closes the doc, e.g. the
 * shipment was delivered). An accepted bid keeps the chat open for text, as in Active chats.
 */
const isBidOpen = (bidStatus?: string | null): boolean =>
  !bidStatus || (CUSTOMER_CHAT_SIDE.activeBidStatuses as readonly string[]).includes(bidStatus);

/** The Active chats listener retries this many times, this far apart, before giving up. */
const ACTIVE_LISTENER_RETRIES = 3;
const ACTIVE_LISTENER_RETRY_DELAY_MS = 5000;

/** Latest activity first; conversations with no time at all go last (contract §7.1). */
const byRecency = (rows: BidChatRow[]): BidChatRow[] => {
  const time = (row: BidChatRow): number => new Date(row.lastMessageAt ?? row.createdAt ?? 0).getTime() || 0;
  return [...rows].sort((a, b) => time(b) - time(a));
};

/** The row's last message is unread, from the bidder, and newer than the one last seen on it. */
const isNewIncoming = (row: BidChatRow, lastMessageAt: Map<string, string | null>): boolean =>
  row.unreadCount > 0 &&
  !!row.lastMessageAt &&
  !!row.lastMessageSenderType &&
  row.lastMessageSenderType !== CUSTOMER_CHAT_SIDE.senderType &&
  row.lastMessageAt !== lastMessageAt.get(row.firestoreConversationId);

/** A History row from a `conversations/history` item (closed, so no unread count). */
const rowFromDto = (dto: ChatConversationDto): BidChatRow => ({
  firestoreConversationId: dto.firestoreConversationId,
  bidStatus: dto.bidStatus ?? null,
  status: dto.status === ChatConversationStatus.Open ? ChatConversationStatus.Open : ChatConversationStatus.Closed,
  shipmentId: dto.shipmentId,
  fromCity: dto.fromCity ?? null,
  toCity: dto.toCity ?? null,
  counterpartName: dto.counterpart?.name || '',
  unreadCount: 0,
  lastMessageAt: toIsoTime(dto.lastMessageAt),
  lastMessagePreview: dto.lastMessagePreview ?? null,
  lastMessageSenderType: dto.lastMessageSenderType ?? null,
});

/**
 * While a new offer is sending, the one on the table already shows what the send will make it
 * (the fleet's → REJECTED, mine → DEACTIVATED), so two offers never wait for acceptance at once.
 * Only a view: if the send fails, the server state shows again.
 */
const closeActiveOffer = (message: BidChatMessage): BidChatMessage =>
  message.type === ChatMessageType.Offer && message.offerStatus === ChatOfferStatus.Active
    ? { ...message, offerStatus: message.senderType === CUSTOMER_CHAT_SIDE.senderType ? ChatOfferStatus.Deactivated : ChatOfferStatus.Rejected }
    : message;

/** initiate-bid answers that no retry can fix: 400 BidAlreadyProcessed, 403 UnauthorizedUser, 404 BidNotFound. */
const PERMANENT_INITIATE_ERRORS = [400, 403, 404];

/** A message this browser sent, kept on screen until Firestore has it (or until it failed). */
interface OutgoingMessage {
  message: BidChatMessage;
  write: (firestoreConversationId: string) => Promise<void>;
}

/**
 * One thread (#47189), all in Firestore. Owned by the thread component: `destroy()` detaches every
 * listener.
 *
 * Sending feels instant: a message shows at once as "sending", turns "sent" when the server
 * confirms it (Firestore's `hasPendingWrites`), and "failed" with a Retry when the write is rejected.
 * Writes go out as soon as the conversation exists, in send order (the SDK keeps it); only
 * `initiate-bid` is shared, so the first messages open the conversation once.
 */
export class BidChatThreadController {
  readonly state$ = new BehaviorSubject<BidChatThreadState>(INITIAL_THREAD_STATE);

  private conversation: ChatConversationRef | null;
  private subscriptions = new Subscription();
  private lastResetAt = 0;
  private serverMessages: BidChatMessage[] = [];
  private readonly outgoing = new Map<string, OutgoingMessage>();
  private creating: Promise<void> | null = null;
  private destroyed = false;
  /** A live listener died: the thread shows the error (Retry) until `retry()` re-attaches. */
  private listenerFailed = false;

  constructor(
    private target: BidChatTarget,
    private api: BidChatApiService,
    private firebase: BidChatFirebaseService,
    private onAccepted: (target: BidChatTarget) => void,
    private onConversationStarted: (target: BidChatTarget, conversation: ChatConversationRef) => void
  ) {
    this.conversation = target.conversation ?? null;
    this.start();
  }

  retry(): void {
    this.detach();
    this.start();
  }

  destroy(): void {
    this.destroyed = true;
    this.detach();
    this.state$.complete();
  }

  send(text: string): void {
    const body = (text || '').trim();
    if (!body) return;
    this.enqueue({ type: ChatMessageType.Text, body }, (conversationId, id) =>
      this.firebase.sendMessage(conversationId, body, id, this.state$.value.header.counterpartKind)
    );
  }

  /**
   * The customer's price. Always a counter-offer: the fleet's bid is the opening offer, so even the
   * customer's first price answers it (design: every own offer reads "You counter-offered").
   */
  sendOffer(price: number): void {
    const offerPrice = Number(price);
    if (!(offerPrice > 0)) return;
    this.enqueue(
      { type: ChatMessageType.Offer, body: offerBody(offerPrice), offerPrice, isCounterOffer: true, offerStatus: ChatOfferStatus.Active },
      (conversationId, id) => this.firebase.sendOffer(conversationId, offerPrice, true, id, this.state$.value.header.counterpartKind)
    );
  }

  /** Sends a failed message again with the same id, so a write that did land is never duplicated. */
  resend(messageId: string): void {
    const item = this.outgoing.get(messageId);
    if (!item || item.message.delivery !== 'failed' || !this.state$.value.isOpen) return;
    item.message = { ...item.message, delivery: 'sending' };
    this.publish();
    this.runInQueue(item);
  }

  /**
   * Accepts the fleet's active offer. Only the backend is called: it finalizes the bid and updates
   * Firestore (offer and bid ACCEPTED), which the live listeners then show — text goes on, offers stop.
   */
  async acceptOffer(offer: BidChatMessage): Promise<void> {
    const conversationId = this.conversation?.firestoreConversationId;
    const { acceptStatus } = this.state$.value;
    if (!offer?.id || !conversationId || acceptStatus === 'accepting' || acceptStatus === 'accepted') return;

    this.patch({ acceptStatus: 'accepting', acceptOfferId: offer.id });
    try {
      await this.api.acceptOffer(conversationId, offer.id).toPromise();
      this.patch({ acceptStatus: 'accepted' });
      this.onAccepted(this.target);
    } catch (error) {
      this.patch({ acceptStatus: 'failed' });
      throw error;
    }
  }

  private enqueue(
    content: Pick<BidChatMessage, 'type' | 'body' | 'offerPrice' | 'isCounterOffer' | 'offerStatus'>,
    write: (firestoreConversationId: string, messageId: string) => Promise<void>
  ): void {
    if (!this.state$.value.isOpen) return;

    // The caller-generated id is the idempotency key: the same id on every retry. `uuid`, not
    // `crypto.randomUUID()`, which needs a secure context (and Safari 15.4+).
    const id = uuid.v4();
    const item: OutgoingMessage = {
      message: { id, senderType: CUSTOMER_CHAT_SIDE.senderType, senderName: '', sentAt: new Date().toISOString(), delivery: 'sending', ...content },
      write: (conversationId) => write(conversationId, id),
    };
    this.outgoing.set(id, item);
    this.publish();
    this.runInQueue(item);
  }

  /**
   * Not awaited one by one: a write resolves only when the server confirms it, so a slow or offline
   * one must not hold back the next. Until then the listener shows it from the local cache as
   * "sending". Callbacks on the shared `ensureConversation()` run in send order, so writes start in order.
   */
  private runInQueue(item: OutgoingMessage): void {
    this.ensureConversation()
      .then(() => item.write(this.conversation.firestoreConversationId))
      .catch((error) => {
        // Also when the local cache had taken it and the server then rejected it (e.g. the rules):
        // Firestore drops it from the listener, so it comes back here, marked failed, with Retry.
        item.message = { ...item.message, delivery: 'failed' };
        this.outgoing.set(item.message.id, item);
        // The bid can't take a conversation any more (the server's reason is in the global error
        // toast): close the composer, so no Retry is offered that would fail the same way.
        if (error instanceof AppError && PERMANENT_INITIATE_ERRORS.includes(error.code)) {
          this.patch({ isOpen: false });
        }
        this.publish();
      });
  }

  /** The customer's first message is what opens the conversation (AC: fleets can't start one). */
  private ensureConversation(): Promise<void> {
    if (this.conversation) return Promise.resolve();
    if (!this.creating) {
      this.creating = (async () => {
        if (!this.target.bidId) throw new Error('No bid to open a conversation for');
        // Idempotent on the backend, so reopening a bid whose list still has no doc id is safe.
        const created = await this.api.initiateBidConversation(this.target.bidId).toPromise();
        if (!created?.firestoreConversationId) throw new Error('Conversation was not created');
        this.conversation = {
          firestoreConversationId: created.firestoreConversationId,
          // Unknown reads as CLOSED, like everywhere else (contract §4.5); the doc listener corrects it.
          status: created.status === ChatConversationStatus.Open ? ChatConversationStatus.Open : ChatConversationStatus.Closed,
        };
        this.onConversationStarted(this.target, this.conversation);
        // Closed while initiate-bid was running: attaching now would leak listeners nobody detaches.
        if (this.destroyed) throw new Error('Thread closed');
        this.attach(this.conversation);
      })().catch((error) => {
        this.creating = null;
        throw error;
      });
    }
    return this.creating;
  }

  private start(): void {
    this.serverMessages = [];
    this.listenerFailed = false;
    this.patch({ ...INITIAL_THREAD_STATE, header: headerFromTarget(this.target), bidStatus: this.target.bidStatus ?? null });

    if (this.conversation) {
      this.attach(this.conversation);
    } else {
      // No document id on the bid: nothing to load, just an empty chat until the first message.
      this.patch({ phase: 'ready', mode: 'new', isOpen: isBidOpen(this.state$.value.bidStatus), messagesLoaded: true });
    }
  }

  private attach(ref: ChatConversationRef): void {
    if (this.destroyed) return;

    // Seeded from a known status so the composer does not flash; with only a doc id (a bid) the
    // thread stays "resolving" until Firestore reports it. Kept live either way.
    if (ref.status) {
      this.applyStatus(ref.status);
    }

    this.subscriptions.add(
      this.firebase.conversation$(ref.firestoreConversationId).subscribe({
        // null = doc swept after close: keep the last known state, or, when nothing is known yet
        // (opened from a bid's doc id), show it as a closed, read-only chat.
        next: (conversation) => {
          if (conversation) {
            // The doc wins; the bid fills in what it lacks.
            this.patch({
              header: { ...headerFromTarget(this.target), ...conversation.header },
              bidStatus: conversation.bidStatus ?? this.state$.value.bidStatus,
            });
            this.applyStatus(conversation.status);
          } else if (!this.state$.value.mode) {
            this.applyStatus(ChatConversationStatus.Closed);
          }
        },
        // Without it the thread can't follow a close or a rejected bid: fail visibly (Retry).
        error: () => this.fail(),
      })
    );

    this.subscriptions.add(
      this.firebase.messages$(ref.firestoreConversationId).subscribe({
        next: (messages) => {
          this.serverMessages = messages;
          // Our own sends are now carried by the listener (with Firestore's pending/sent state).
          messages.forEach((message) => this.outgoing.delete(message.id));
          this.publish({ messagesLoaded: true, ...(this.state$.value.mode && !this.listenerFailed ? { phase: 'ready' } : {}) });
          if (this.state$.value.isOpen) {
            this.resetUnreadIfNewer(ref.firestoreConversationId, messages);
          }
        },
        error: () => this.fail(),
      })
    );
  }

  private fail(): void {
    this.listenerFailed = true;
    this.patch({ phase: 'error' });
  }

  /** Server messages, then this browser's sends that Firestore does not have yet, in time order. */
  private publish(extra: Partial<BidChatThreadState> = {}): void {
    const onServer = new Set(this.serverMessages.map((message) => message.id));
    const pending = [...this.outgoing.values()].map((item) => item.message).filter((message) => !onServer.has(message.id));
    const replacing = pending.some((message) => message.type === ChatMessageType.Offer && message.delivery === 'sending');
    const server = replacing ? this.serverMessages.map(closeActiveOffer) : this.serverMessages;
    const messages = [...server, ...pending].sort((a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime());
    this.patch({ ...extra, messages });
  }

  /** The conversation can close while it is on screen: the composer turns read-only live. */
  private applyStatus(status: ChatConversationStatus): void {
    const wasOpen = this.state$.value.isOpen;
    const isOpen = status === ChatConversationStatus.Open && isBidOpen(this.state$.value.bidStatus);
    // A dead listener keeps the error screen: a later doc update must not hide Retry.
    this.patch({ phase: this.listenerFailed ? 'error' : 'ready', isOpen, mode: isOpen ? 'live' : 'history' });
    // Messages can land before the doc says the chat is open: reset unread now, not on the next message.
    if (isOpen && !wasOpen && this.conversation) {
      this.resetUnreadIfNewer(this.conversation.firestoreConversationId, this.serverMessages);
    }
  }

  /**
   * Covers both "opened the chat" and "a message arrived while it is open" (contract §7.2). Only the
   * other side's messages raise my counter, so my own sends never cost a reset write.
   */
  private resetUnreadIfNewer(firestoreConversationId: string, messages: BidChatMessage[]): void {
    const incoming = messages.filter((message) => message.senderType !== CUSTOMER_CHAT_SIDE.senderType);
    const newest = incoming.length ? new Date(incoming[incoming.length - 1].sentAt).getTime() : 0;
    if (newest > this.lastResetAt) {
      this.lastResetAt = newest;
      this.firebase.resetMyUnread(firestoreConversationId);
    }
  }

  private patch(partial: Partial<BidChatThreadState>): void {
    if (!this.state$.isStopped) {
      this.state$.next({ ...this.state$.value, ...partial });
    }
  }

  private detach(): void {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
  }
}

/**
 * Bid chat facade (#47189): the customer's Chat logs, the header unread badge, and one thread's
 * controller. Components talk only to this; Firestore and the backend stay behind it.
 */
@Injectable({ providedIn: 'root' })
export class BidChatService {
  /**
   * Header badge: the customer's unread total over Active chats (contract §8), from the shared
   * listener — so a new login never inherits the previous account's inbox.
   */
  readonly unreadCount$: Observable<number>;

  /**
   * The one live Active chats listener, newest first, shared by the header badge and Chat logs and
   * restarted per customer. A failed listener reads as empty instead of ending the stream.
   */
  private readonly activeRows$: Observable<BidChatRow[]>;

  private readonly offerAcceptedSubject$ = new Subject<BidChatTarget>();
  /**
   * An offer accepted from inside a thread: the bid is final and the shipment now has its carrier,
   * so the page showing that shipment reloads, as after an accept from the bids list.
   */
  readonly offerAccepted$ = this.offerAcceptedSubject$.asObservable();

  private readonly incomingMessageSubject$ = new Subject<BidChatRow>();
  /**
   * An Active chat whose last message is a new one from the bidder, seen on the live listener as soon
   * as it is written: well before its push arrives. Only while the listener runs (the header badge).
   */
  readonly incomingMessage$ = this.incomingMessageSubject$.asObservable();

  constructor(
    private api: BidChatApiService,
    private firebase: BidChatFirebaseService,
    private userApi: UserApi,
    private drawerService: BidChatDrawerService
  ) {
    this.activeRows$ = this.userApi.currentUser$.pipe(
      map((user) => user?.parentId ?? null),
      distinctUntilChanged(),
      switchMap((customerId) => (customerId ? this.liveActiveRows$() : of([] as BidChatRow[]))),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.unreadCount$ = this.activeRows$.pipe(map((rows) => rows.reduce((total, row) => total + row.unreadCount, 0)));
  }

  /**
   * Chat logs. Active chats: live from Firestore (`status == OPEN`, `bidStatus in [PENDING, ACCEPTED]`,
   * `customerId == parentId`). History: `GET conversations/history`. When a chat leaves the live list
   * (closed, rejected or cancelled), History is re-fetched silently so it moves there (contract §7.1).
   */
  logs$(): Observable<BidChatLogsState> {
    const leftActive$ = new Subject<void>();
    let previousIds = new Set<string>();

    // The shared listener: while the badge is on screen, Chat logs opens with its rows at once.
    const active$ = this.activeRows$.pipe(
      tap((rows) => {
        const ids = new Set(rows.map((row) => row.firestoreConversationId));
        if ([...previousIds].some((id) => !ids.has(id))) leftActive$.next();
        previousIds = ids;
      }),
      startWith(null as BidChatRow[] | null)
    );
    const history$ = merge(of(null), leftActive$).pipe(
      switchMap(() => this.historyRows$()),
      startWith(null as BidChatRow[] | null)
    );

    return combineLatest([active$, history$]).pipe(
      map(([active, history]) => {
        const activeIds = new Set((active ?? []).map((row) => row.firestoreConversationId));
        return {
          active: active ?? [],
          // Until History catches up, a chat can briefly be in both: list it once, as active.
          closed: byRecency((history ?? []).filter((row) => !activeIds.has(row.firestoreConversationId))),
          hasActiveSnapshot: active !== null,
          hasClosedSnapshot: history !== null,
        };
      })
    );
  }

  /**
   * Opens the chat a notification points at (a push for a new message): its thread when it is one
   * of the customer's Active chats, by conversation, else by shipment when only one bidder chats on
   * it; otherwise Chat logs (a closed chat is only in History, and several bidders can't be told apart).
   */
  openChat({ conversationId, shipmentId }: { conversationId?: string | null; shipmentId?: number | null }): void {
    this.activeRows$.pipe(take(1)).subscribe((rows) => {
      const byShipment = rows.filter((candidate) => !!shipmentId && String(candidate.shipmentId) === String(shipmentId));
      const row =
        rows.find((candidate) => !!conversationId && candidate.firestoreConversationId === conversationId) ||
        (byShipment.length === 1 ? byShipment[0] : null);
      if (row) {
        this.drawerService.openThread(targetFromRow(row), true);
      } else {
        this.drawerService.openLogs();
      }
    });
  }

  openThread(target: BidChatTarget): BidChatThreadController {
    return new BidChatThreadController(
      target,
      this.api,
      this.firebase,
      (accepted) => this.offerAcceptedSubject$.next(accepted),
      (started, conversation) => this.drawerService.conversationStarted(started, conversation.firestoreConversationId)
    );
  }

  /**
   * The Active chats listener, kept alive: an error is retried a few times with a pause, and if it
   * still fails the last rows stay on screen (contract §8: keep the last known value). Only when
   * nothing ever arrived does it fall back to an empty list, so the UI never waits forever.
   */
  private liveActiveRows$(): Observable<BidChatRow[]> {
    return defer(() => {
      let received = false;
      // Last message time per conversation. Kept across retries, so a re-attached listener's first
      // snapshot doesn't announce messages already seen.
      const lastMessageAt = new Map<string, string | null>();
      return this.firebase.activeInbox$().pipe(
        map(byRecency),
        tap((rows) => {
          // The first snapshot is the baseline: what is unread then is old news.
          if (received) {
            rows.filter((row) => isNewIncoming(row, lastMessageAt)).forEach((row) => this.incomingMessageSubject$.next(row));
          }
          rows.forEach((row) => lastMessageAt.set(row.firestoreConversationId, row.lastMessageAt ?? null));
          received = true;
        }),
        retryWhen((errors) =>
          errors.pipe(
            scan((attempt, error) => {
              if (attempt >= ACTIVE_LISTENER_RETRIES) throw error;
              return attempt + 1;
            }, 0),
            delay(ACTIVE_LISTENER_RETRY_DELAY_MS)
          )
        ),
        catchError(() => (received ? EMPTY : of([] as BidChatRow[])))
      );
    });
  }

  /** History from `GET conversations/history` (CLOSED only); a failed call reads as empty. */
  private historyRows$(): Observable<BidChatRow[]> {
    return this.api.getHistory().pipe(
      map(({ items }) => (items || []).filter((dto) => !!dto?.firestoreConversationId).map(rowFromDto)),
      catchError(() => of([] as BidChatRow[]))
    );
  }
}
